import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Search } from "../../UI/Search/Search";
import { BreadcrumbAdmin } from "../../UI/BreadcrumbAdmin";
import ProgressBar from "../../UI/ProgressBar";
import { useGetAllCompaniesQuery } from "../../../redux-toolkit/features/company/companyApiSlice";
import { useGetBooksQuery } from "../../../redux-toolkit/features/books/bookApiSlice";
import {
  useGetPromotionInvitesQuery,
  useSendPromotionInviteMutation,
  useCancelPromotionInviteMutation,
  useDeletePromotionInviteMutation,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { debounce } from "../../../utils";
import { useAlert } from "../../../hooks/useAlert";
import { useOutsideClick } from "../../../hooks/useOutsideClick";
import {
  canCancelPromotionInvite,
  hasPromotionEventElapsed,
} from "../../../constants/promotionStatuses";
import {
  formatPromotionEventTime,
  formatUtcEventDate,
} from "./promotionEventDateTime";
import { EditPromotionInviteModal } from "./EditPromotionInviteModal";
import { SendInviteConfirmModal } from "./SendInviteConfirmModal";
import { CancelEventConfirmModal } from "./CancelEventConfirmModal";
import { DeleteEventConfirmModal } from "./DeleteEventConfirmModal";
import { CompanyFilterDropdown } from "./CompanyFilterDropdown";
import { getMissingInviteSendFields } from "./promotionInviteFields";
import {
  DEFAULT_HISTORY_DATE_FILTER_FIELD,
  DEFAULT_HISTORY_FIELD_CHECK,
  HISTORY_EMPTY_FIELDS_COPY,
  HISTORY_FIELD_LIST,
  HISTORY_LIST_TABS,
  allHistoryDataFieldsHidden,
  buildHistoryDateQueryArgs,
  readHistoryListFilters,
  writeHistoryListSearch,
  isHistoryFieldVisibleOnTab,
  visibleHistoryColSpan,
} from "./promotionHistoryColumns";
import { PromotionHistoryDateFilter } from "./PromotionHistoryDateFilter";
import "./Promotions.css";
import "../../Admin/Reports/Reports Detailed/ReportsDetailed.css";

const DEFAULT_TABS = HISTORY_LIST_TABS;

const EVENT_STATUS_PILL_CLASS = {
  COMPLETED: "promo-pill--completed",
  CLOSED: "promo-pill--canceled",
};

export const PromotionsHistory = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFiltersRef = useRef(null);
  if (!initialFiltersRef.current) {
    initialFiltersRef.current = readHistoryListFilters(searchParams);
  }
  const initialFilters = initialFiltersRef.current;
  const { displayAlert } = useAlert();
  const { data: companiesData } = useGetAllCompaniesQuery();
  const { data: booksData } = useGetBooksQuery("org");
  const companiesRaw =
    companiesData?.data?.companies || companiesData?.data || [];
  const companies = Array.isArray(companiesRaw) ? companiesRaw : [];
  const booksRaw = booksData?.data?.books || booksData?.data || [];
  const books = Array.isArray(booksRaw)
    ? [...booksRaw].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];

  const [tab, setTab] = useState(initialFilters.tab);
  const [searchInput, setSearchInput] = useState(initialFilters.search);
  const [searchQuery, setSearchQuery] = useState(initialFilters.search);
  const [companyIds, setCompanyIds] = useState(initialFilters.companyIds);
  const [bookIds, setBookIds] = useState(initialFilters.bookIds);
  const [dateField, setDateField] = useState(initialFilters.dateField);
  const [dateStart, setDateStart] = useState(initialFilters.dateStart);
  const [dateEnd, setDateEnd] = useState(initialFilters.dateEnd);
  const [select, setSelect] = useState(false);
  const [check, setCheck] = useState(() => ({ ...DEFAULT_HISTORY_FIELD_CHECK }));
  const fieldsRef = useRef(null);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuInvite, setMenuInvite] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [editInvite, setEditInvite] = useState(null);
  const [missingSendFields, setMissingSendFields] = useState([]);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendInviteTarget, setSendInviteTarget] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [sendInvite, { isLoading: isSending }] =
    useSendPromotionInviteMutation();
  const [cancelInvite, { isLoading: isCancelling }] =
    useCancelPromotionInviteMutation();
  const [deleteInvite, { isLoading: isDeleting }] =
    useDeletePromotionInviteMutation();

  const setDebouncedSearch = useMemo(
    () => debounce((value) => setSearchQuery(value), 400),
    []
  );

  const listSearch = writeHistoryListSearch({
    tab,
    search: searchQuery,
    companyIds,
    bookIds,
    dateField,
    dateStart,
    dateEnd,
  });

  useEffect(() => {
    const nextQuery = listSearch.replace(/^\?/, "");
    if (searchParams.toString() !== nextQuery) {
      setSearchParams(nextQuery, { replace: true });
    }
  }, [listSearch, searchParams, setSearchParams]);

  const closeFieldPopup = useCallback(() => setSelect(false), []);
  useOutsideClick(fieldsRef, closeFieldPopup);

  const handleFieldChange = (event) => {
    setCheck((prev) => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }));
  };

  const inviteQueryArgs = {
    status: tab,
    companyIds: companyIds.length ? companyIds : undefined,
    bookIds: bookIds.length ? bookIds : undefined,
    search: searchQuery || undefined,
    ...buildHistoryDateQueryArgs({
      field: dateField,
      startDate: dateStart,
      endDate: dateEnd,
    }),
    page: 1,
    limit: 200,
  };

  const { data, isLoading, isFetching, error, refetch } =
    useGetPromotionInvitesQuery(inviteQueryArgs, {
      refetchOnMountOrArgChange: true,
    });

  const tabs = data?.data?.tabs?.length ? data.data.tabs : DEFAULT_TABS;
  const invites = data?.data?.invites?.list || [];

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuInvite(null);
  };

  const openMenu = (event, invite) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuInvite(invite);
  };

  const openEvent = (inviteId) => {
    navigate(`/super-admin/promotions/history/${inviteId}`, {
      state: { listSearch },
    });
  };

  const handleViewEvent = () => {
    const id = menuInvite?._id;
    closeMenu();
    if (id) openEvent(id);
  };

  const handleSendInvite = () => {
    const invite = menuInvite;
    closeMenu();
    if (!invite) return;
    if (invite.status !== "PROMOTION_CREATED") {
      displayAlert({
        open: true,
        message: "Invites can only be sent for events in Promotion Created",
        type: "info",
        timeout: "2500",
      });
      return;
    }
    if (!(invite.employeeCount > 0)) {
      displayAlert({
        open: true,
        message: "Add at least one employee before sending invites",
        type: "info",
        timeout: "2500",
      });
      return;
    }
    const missing = getMissingInviteSendFields(invite);
    if (missing.length) {
      setEditInvite(invite);
      setMissingSendFields(missing);
      setEditOpen(true);
      return;
    }
    setSendInviteTarget(invite);
    setSendOpen(true);
  };

  const handleEditEvent = () => {
    const invite = menuInvite;
    closeMenu();
    if (!invite) return;
    if (invite.status !== "PROMOTION_CREATED") {
      displayAlert({
        open: true,
        message: "Only events in Promotion Created can be edited",
        type: "info",
        timeout: "2500",
      });
      return;
    }
    setMissingSendFields([]);
    setEditInvite(invite);
    setEditOpen(true);
  };

  const handleCancelEvent = () => {
    const invite = menuInvite;
    closeMenu();
    if (!invite) return;
    if (!canCancelPromotionInvite(invite)) {
      displayAlert({
        open: true,
        message:
          invite.status !== "INVITE_SENT"
            ? "Cancel is only available after invites are sent"
            : hasPromotionEventElapsed(invite.eventDate, new Date(), {
                hasEventTime: invite.hasEventTime !== false,
              })
              ? "Events cannot be cancelled after the event date"
              : "Unable to cancel event",
        type: "info",
        timeout: "2500",
      });
      return;
    }
    setCancelTarget(invite);
    setCancelOpen(true);
  };

  const handleDeleteEvent = () => {
    const invite = menuInvite;
    closeMenu();
    if (!invite) return;
    setDeleteTarget(invite);
    setDeleteOpen(true);
  };

  const confirmCancelEvent = async () => {
    if (!cancelTarget?._id) return;
    try {
      await cancelInvite({ inviteId: cancelTarget._id }).unwrap();
      displayAlert({
        open: true,
        message: "Event cancelled",
        type: "success",
        timeout: "2500",
      });
      setCancelOpen(false);
      setCancelTarget(null);
      refetch();
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || err?.message || "Unable to cancel event",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const confirmDeleteEvent = async () => {
    if (!deleteTarget?._id) return;
    try {
      await deleteInvite({ inviteId: deleteTarget._id }).unwrap();
      displayAlert({
        open: true,
        message: "Event deleted",
        type: "success",
        timeout: "2500",
      });
      setDeleteOpen(false);
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || err?.message || "Unable to delete event",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const confirmSendInvite = async () => {
    if (!sendInviteTarget?._id) return;
    try {
      await sendInvite({ inviteId: sendInviteTarget._id }).unwrap();
      displayAlert({
        open: true,
        message: "Promotion invites sent successfully",
        type: "success",
        timeout: "2500",
      });
      setSendOpen(false);
      setSendInviteTarget(null);
      refetch();
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || err?.message || "Unable to send invites",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const canSend = menuInvite?.status === "PROMOTION_CREATED";
  const canEdit = menuInvite?.status === "PROMOTION_CREATED";
  const canCancel = canCancelPromotionInvite(menuInvite);
  const canDelete =
    menuInvite && menuInvite.status === "PROMOTION_CREATED";
  const showField = (key) =>
    Boolean(check[key]) && isHistoryFieldVisibleOnTab(key, tab);
  const colSpan = visibleHistoryColSpan(check, tab);
  const fieldsHidden = allHistoryDataFieldsHidden(check, tab);
  const emptyCopy = useMemo(() => {
    if (fieldsHidden) return HISTORY_EMPTY_FIELDS_COPY;
    if (error) return "Unable to load promotion events";
    return `No promotion events in “${
      tabs.find((item) => item.value === tab)?.label || tab
    }”`;
  }, [error, fieldsHidden, tab, tabs]);

  return (
    <div className="promotions-history-shell container">
      <BreadcrumbAdmin
        base={[
          {
            _id: "promotions",
            link: "/super-admin/promotions",
            label: "Promotions",
          },
        ]}
        page="Manage Events"
      />

      <div className="promotions-page__toolbar">
        <div className="promotions-page__search">
          <Search
            placehold="Search.."
            searchVal={searchInput}
            setSearchVal={(val) => {
              setSearchInput(val);
              setDebouncedSearch(val);
            }}
          />
        </div>
        <div className="promotions-page__filters">
          <PromotionHistoryDateFilter
            field={dateField}
            startDate={dateStart}
            endDate={dateEnd}
            onApply={({ field, startDate, endDate }) => {
              setDateField(field);
              setDateStart(startDate);
              setDateEnd(endDate);
            }}
            onReset={() => {
              setDateField(DEFAULT_HISTORY_DATE_FILTER_FIELD);
              setDateStart(null);
              setDateEnd(null);
            }}
          />
          <CompanyFilterDropdown
            companies={books}
            appliedIds={bookIds}
            onApply={setBookIds}
            variant="toolbar"
            itemLabel="Book"
            itemLabelPlural="Books"
            searchPlaceholder="Search books..."
            allLabel="All books"
            emptyLabel="No books found"
            ariaLabel="Filter by book"
          />
          <CompanyFilterDropdown
            companies={companies}
            appliedIds={companyIds}
            onApply={setCompanyIds}
            variant="toolbar"
          />
          <div ref={fieldsRef} className="reports__selectLayout">
            <Button
              variant="outlined"
              className="promotions-page__manage-field"
              endIcon={
                <ArrowForwardIosIcon
                  sx={{
                    transform: select ? "rotate(-90deg)" : "rotate(90deg)",
                    color: "grey",
                    fontSize: "16px !important",
                  }}
                />
              }
              onClick={() => setSelect(!select)}
            >
              Manage Field
            </Button>
            <div className={select ? "reports__selectActive" : "reports__select"}>
              <div className="reports__selectcontent">
                {HISTORY_FIELD_LIST.filter((item) =>
                  isHistoryFieldVisibleOnTab(item.name, tab)
                ).map((item) => (
                  <FormControlLabel
                    key={item.name}
                    control={
                      <Checkbox
                        checked={Boolean(check[item.name])}
                        name={item.name}
                        onChange={handleFieldChange}
                        sx={{ "& .MuiSvgIcon-root": { fontSize: "20px" } }}
                      />
                    }
                    label={
                      <span style={{ fontSize: "15px" }}>{item.label}</span>
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="promotions-page__tabs-row">
        <Tabs
          value={tab}
          onChange={(_e, value) => setTab(value)}
          className="promotions-page__tabs"
        >
          {tabs.map((item) => (
            <Tab key={item.value} label={item.label} value={item.value} />
          ))}
        </Tabs>
      </div>

      <div className="promotions-page__table-wrap">
        {(isLoading || isFetching || isCancelling || isDeleting) && (
          <ProgressBar />
        )}
        <table className="promotions-table">
          <thead>
            <tr>
              {showField("name") && <th>Promotion Name</th>}
              {showField("book") && <th>Book Name</th>}
              {showField("createdBy") && <th>Created By</th>}
              {showField("createdOn") && <th>Created On</th>}
              {showField("eventDate") && <th>Promotion Date</th>}
              {showField("eventTime") && <th>Promotion Time</th>}
              {showField("venue") && <th>Promotion Venue</th>}
              {showField("status") && <th>Promotion Status</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {fieldsHidden || invites.length === 0 ? (
              <tr>
                <td colSpan={colSpan} className="promotions-table__empty">
                  {emptyCopy}
                </td>
              </tr>
            ) : (
              invites.map((row) => (
                  <tr
                    key={row._id}
                    className="promotions-table__row--clickable"
                    onClick={() => openEvent(row._id)}
                  >
                    {showField("name") && <td>{row.name || "—"}</td>}
                    {showField("book") && <td>{row.book?.name || "—"}</td>}
                    {showField("createdBy") && (
                      <td>{row.createdBy?.name || "—"}</td>
                    )}
                    {showField("createdOn") && (
                      <td>{formatUtcEventDate(row.createdOn, { shortYear: true })}</td>
                    )}
                    {showField("eventDate") && (
                      <td>{formatUtcEventDate(row.eventDate, { shortYear: true })}</td>
                    )}
                    {showField("eventTime") && (
                      <td>
                        {row.hasEventTime === false
                          ? "—"
                          : formatPromotionEventTime(row.eventDate)}
                      </td>
                    )}
                    {showField("venue") && <td>{row.venue || "—"}</td>}
                    {showField("status") && (
                      <td>
                        <span
                          className={`promo-pill ${
                            EVENT_STATUS_PILL_CLASS[row.status] || ""
                          }`}
                        >
                          {row.statusLabel || row.status}
                        </span>
                      </td>
                    )}
                    <td onClick={(e) => e.stopPropagation()}>
                      {tab === "CLOSED" ? (
                        <IconButton
                          size="small"
                          aria-label="View event"
                          onClick={() => openEvent(row._id)}
                        >
                          <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      ) : (
                        <IconButton
                          size="small"
                          aria-label="Row actions"
                          onClick={(e) => openMenu(e, row)}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </td>
                  </tr>
                ))
            )}
          </tbody>
        </table>
      </div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ className: "promo-history-actions-menu" }}
      >
        <MenuItem onClick={handleViewEvent}>View Event</MenuItem>
        {canSend && (
          <MenuItem onClick={handleSendInvite}>Send Invite</MenuItem>
        )}
        {canEdit && (
          <MenuItem onClick={handleEditEvent}>Edit Event</MenuItem>
        )}
        {canCancel && (
          <MenuItem
            onClick={handleCancelEvent}
            className="promo-history-actions-menu__danger"
          >
            Cancel Event
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem
            onClick={handleDeleteEvent}
            className="promo-history-actions-menu__danger"
          >
            Delete Event
          </MenuItem>
        )}
      </Menu>

      <EditPromotionInviteModal
        open={editOpen}
        onClose={() => {
          setEditOpen(false);
          setEditInvite(null);
          setMissingSendFields([]);
        }}
        invite={editInvite}
        missingSendFields={missingSendFields}
        onSuccess={() => refetch()}
      />

      <SendInviteConfirmModal
        open={sendOpen}
        onClose={() => {
          if (isSending) return;
          setSendOpen(false);
          setSendInviteTarget(null);
        }}
        onConfirm={confirmSendInvite}
        confirming={isSending}
      />

      <CancelEventConfirmModal
        open={cancelOpen}
        onClose={() => {
          if (isCancelling) return;
          setCancelOpen(false);
          setCancelTarget(null);
        }}
        onConfirm={confirmCancelEvent}
        confirming={isCancelling}
      />

      <DeleteEventConfirmModal
        open={deleteOpen}
        onClose={() => {
          if (isDeleting) return;
          setDeleteOpen(false);
          setDeleteTarget(null);
        }}
        onConfirm={confirmDeleteEvent}
        confirming={isDeleting}
      />
    </div>
  );
};
